import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UtilityService } from '../../services/utility.service';
import { forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ILogger } from '../../shared/models/logger.interface';
import { Title } from '@angular/platform-browser';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AccountService } from 'src/app/services/account.service';
import { environment as ENV, environment } from '../../../environments/environment';
import en from 'src/assets/i18n/en.json';
import { UserService } from 'src/app/services/user.service';
import { TaggingService } from 'src/app/tags/tagging.service';
import { BillingService } from 'src/app/services/billing.service';
import { ALPHA_NUMERIC_AND_SPACES_PATTERN } from 'src/app/shared/regex-patterns';
import { PolicyFormatService } from 'src/app/services/policy-format.service';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { LoginService } from 'src/app/services/login.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'upsc-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponent implements OnInit {

  //Forms
  accountFormGroup: UntypedFormGroup;
  multiPolicyFormGroup: UntypedFormGroup;

  //Display helpers
  systemError = false;
  userCreatedSuccessfully = false;
  errorReturned: boolean = false;
  isCanadaUser: boolean;
  isUkUser: boolean;
  isUsaUser: boolean;
  isGermanyUser: boolean;
  isFranceUser: boolean;
  isItalyUser: boolean;
  isChecked = false;
  showAdminToggle = false;
  apiInProgress = false;
  scriptError: boolean = false;
  dataError: boolean = false;
  showMultiPolicy: boolean = false;
  showPolicy3: boolean = false;
  showAddPolicy3: boolean = true;
  multiPolicySaveHadAnError = false;
  multiPolicySaveApprovalError = false;
  isFlexAppUser: boolean = false;
  isInvalidData: boolean = false;
  isNullData: boolean = false;

  //Data
  arrayOfLogics: any[] = []; //multipolicyregistration
  userDetails;
  logInterface: ILogger = {};
  countryLocal: any;
  multipolicyError1: string = '';
  multipolicyError2: string = '';
  multipolicyError3: string = '';
  auth0locale;
  locale;

  //Links
  privacyLink = ENV.links.privacyNotice;
  canadaprivacyLink = ENV.links.canadaprivacyNotice;
  ukprivacyNotice = ENV.links.ukprivacyNotice;
  deprivacyNotice = ENV.links.deprivacyNotice;
  itprivacyNotice = ENV.links.itprivacyNotice;
  frprivacyNotice = ENV.links.frprivacyNotice;
  techAgreementLink = ENV.links.techAgreement;
  itTechAgreementLink = ENV.links.itItTechAgreementLink;
  frTechAgreementLink = ENV.links.frFrTechAgreementLink;
  deTechAgreementLink = ENV.links.deDeTechAgreementLink;

  user = {
    userObjectId: "",
    policyNumber: "",
    emailAddress: "",
    zipCode: "",
    firstName: "",
    lastName: "",
    userName: "",
    password: "",
    userRole: "",
    appRoleId: "",
    approved: false,
    emailFor: `${ENV.createRequest.emailFor}`,
    roleName: ""
  };

  constructor(
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private titleService: Title,
    private userService: UserService,
    private utilityService: UtilityService,
    private accountService: AccountService,
    private router: Router,
    private taggingService: TaggingService,
    private route: ActivatedRoute,
    private billingService: BillingService,
    private policyFormatService: PolicyFormatService,
    private auth: AuthService,
    private loginService: LoginService
  ) {
    this.determineLocale();
    this.isCanadaUser = this.userService.isCanadaUser();
    this.isUkUser = this.userService.isUkUser();
    this.isUsaUser = this.userService.isUsaUser();
    this.isGermanyUser = this.userService.isGermanyUser();
    this.isFranceUser = this.userService.isFranceUser();
    this.isItalyUser = this.userService.isItalyUser();
    this.userDetails = this.userService.getUserInfo();

    this.route.queryParams.subscribe(params => {
      if (params) {
        this.countryLocal = params.countryLocale;
        if (this.countryLocal) {
          sessionStorage.setItem('locale', this.countryLocal);
        }
        if (params.type == 'flexApp') {
          this.isFlexAppUser = true;
        }
        this.router.navigate(['/create-account']);
      }
    });

    this.accountFormGroup = this.formBuilder.group({
      policyNumberFormControl: ['', [Validators.required, Validators.minLength(6), Validators.pattern(/^(?!(623876))/)]],
      policy2: [''],
      policy3: [''],
      zipFormControl: ['', this.isCanadaUser ? [Validators.required, Validators.pattern(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/)] : this.isUkUser ? [Validators.required, Validators.pattern(/^[A-Za-z0-9\s]{2,8}$/)] : [Validators.required, Validators.pattern(/\d{5}/)]],
      zip2: [''],
      zip3: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      checkBoxFormControl: ['', [Validators.required]],
      adminControl: ['']
    });
  } //End of constructor

  ngOnInit() {
    this.titleService.setTitle(en.createAccount.setTitle);
    this.taggingService.view({
      step: 'account_information',
      journey_step_number: '1',
      journey: 'Sign Up'
    });
    this.determineLoginState();
    this.loadLinksFromLocale();
  }

  formatPolicy() {
    let policy = this.accountFormGroup.controls.policyNumberFormControl.value;
    this.accountFormGroup.controls.policyNumberFormControl.setValue(this.policyFormatService.formatPolicy(policy));
  }

  formatAdditionalPolicy(currentPolicy: string) {
    if (currentPolicy === 'policy1') {
      this.policy.setValue(this.policyFormatService.formatPolicy(this.policy.value));
    } else if (currentPolicy === 'policy2') {
      this.policy2.setValue(this.policyFormatService.formatPolicy(this.policy2.value));
    } else {
      this.policy3.setValue(this.policyFormatService.formatPolicy(this.policy3.value));
    }
  }

  // checkEmail() {
  //   if (!this.accountFormGroup.valid && !this.multiPolicyFormGroup.valid) {
  //     this.accountFormGroup.markAllAsTouched();
  //     this.multiPolicyFormGroup.markAllAsTouched();
  //     return;
  //   }
  //   //Clean slate
  //   this.arrayOfLogics = [];
  //   this.apiInProgress = true;
  //   // this.existingObjectID = null;
  //   // this.existingUserName = null;
  //   this.systemError = false;
  //   this.errorReturned = false;
  //   // this.reinvitedUserFlow = false;
  //   this.multipolicyError1 = '';
  //   this.multipolicyError2 = '';
  //   this.multipolicyError3 = '';

  //   this.taggingService.link({
  //     link_name: 'send_verification_email'
  //   });

  //   // this.policyValidate();
  // }

  //(3)
  checkRoleUsersAndDigitalWallet() {
    let observableArray = [];
    //If it is a USA user, check the number of users registered already, and check for digital wallet
    let defaultPolicy = this.policy.value;
    observableArray.push(this.accountService.checkRoleUsers(defaultPolicy.trim()));

    if (this.showMultiPolicy) {
      observableArray.push(this.accountService.checkRoleUsers(this.policy2.value.trim()));
      if (this.showPolicy3) {
        observableArray.push(this.accountService.checkRoleUsers(this.policy3.value.trim()));
      }
    }

    //Checks the number of users on a policy
    forkJoin(observableArray).subscribe(
      ([resp1, resp2, resp3]) => {
        let res1: any = resp1;
        let res2: any = resp2;
        let res3: any = resp3;
        let arrayOfResponses = [res1?.data, res2?.data, res3?.data];

        //For each policy, assign values based on if there are already users registered or not
        for (let i = 0; i < arrayOfResponses.length; i++) {
          if (arrayOfResponses[i] && arrayOfResponses[i].numberOfUsers > 0) {
            if (!this.showMultiPolicy) {  //If not a multi policy registration, and there was more than 1 user already registered, show them the admin toggle
              this.showAdminToggle = true;
            }
            this.arrayOfLogics[i].allowedToEarnDWPoints = false;
            this.arrayOfLogics[i].approved = false;
            this.arrayOfLogics[i].active = true;
          } else if (arrayOfResponses[i]) {
            this.arrayOfLogics[i].allowedToEarnDWPoints = 'uncertain';
            this.arrayOfLogics[i].approved = true;
            this.arrayOfLogics[i].active = true;
          }
        }

        if (this.arrayOfLogics[0].approved) {
          this.user.approved = true;
        } else {
          this.user.approved = false;
        }

        //Prepare request for checking Digital Wallet
        let request = {
          productSourceSystem: "gw",
          sourceSystemIdentifierName: "PolicyNumber",
          sourceSystemIdentifier: '',
          country: "US",
          roleType: "string",
          productType: "string",
        }

        let dwObservables = [];

        //If there were any policies that we could not determine if they could earn DW points based on # of users registered, go here.
        for (let j = 0; j < this.arrayOfLogics.length; j++) {
          if (this.arrayOfLogics[j].allowedToEarnDWPoints == 'uncertain') {
            request.sourceSystemIdentifier = this.arrayOfLogics[j].policy.trim();
            dwObservables.push(this.billingService.DWreviewEligibility(request));
          }
        }

        //If we need to check the DW API because of uncertain policies - go here.
        if (dwObservables.length > 0) {
          forkJoin(dwObservables).subscribe(
            ([resp1, resp2, resp3]) => {
              let res1: any = resp1;
              let res2: any = resp2;
              let res3: any = resp3;
              let arrayOfResponses = [res1?.data, res2?.data, res3?.data];

              for (let k = 0; k < arrayOfResponses.length; k++) {
                if (arrayOfResponses[k] && arrayOfResponses[k].wallet?.walletID && arrayOfResponses[k].wallet?.rewardEligible == 'True') {
                  if (this.arrayOfLogics[k]) {
                    this.arrayOfLogics[k].allowedToEarnDWPoints = true; //They are the first account to be created under a specific policy number, and have a wallet + rewards eligible so display the earn points message at the end.
                  }
                } else {
                  if (this.arrayOfLogics[k]) {
                    this.arrayOfLogics[k].allowedToEarnDWPoints = false;  //They are the first account to be created under a specific policy number but don't have a wallet/aren't eligible for rewards so don't display the message.
                  }
                }
              }

              this.setupSubmitUser();
            }, error => {
              this.arrayOfLogics.forEach(function (x) {
                if (x.allowedToEarnDWPoints == 'uncertain') {
                  x.allowedToEarnDWPoints = false;  //Default to the not eligible message when this API errors out (IB/TrueCore handles the logic for actually giving the user points)
                }
              });
              this.setupSubmitUser();
            }
          );
        } else {
          this.setupSubmitUser();
        }
      }, error => {
        if ((error.error != null && error.error.errorMessage !== undefined) && ((error.error.errorMessage === 'InvalidInput for field/s.') || (error.error.errorMessage === 'Invalid Input'))) {
          this.scriptError = true;
          this.systemError = false;
          this.apiInProgress = false;
        } else {
          this.scriptError = false;
          this.systemError = true;
          this.apiInProgress = false;
        }
      }
    );
  }

  //(1)
  submit() {
    this.apiInProgress = true;
    this.dataError = false;
    this.errorReturned = false;
    this.isInvalidData = false;
    this.isNullData = false;
    this.systemError = false;
    this.scriptError = false;
    this.arrayOfLogics = [];
    this.multipolicyError1 = '';
    this.multipolicyError2 = '';
    this.multipolicyError3 = '';

    if (!this.accountFormGroup.valid) {
      this.accountFormGroup.markAllAsTouched();
      this.apiInProgress = false;
      return;
    }
    this.policyValidate();
  }

  //(4)Creates a User!
  setupSubmitUser() {
    let request = {
      policyNumber: this.policy.value.trim(),
      email: this.userDetails.emailAddress,
      zip: this.zip.value ? this.zip.value.trim() : "none",
      firstName: this.fName.value,
      lastName: this.lName.value,
      countryLocale: sessionStorage.getItem('locale'),
      additionalPolicies: [] as { policyNumber: string; zipCode: string; approve: boolean | null; active: boolean | null }[]
    }
    if (this.showMultiPolicy) {//Check if this is multi policy user to append more policy to request
      let arrayOfLogicsNoUndefined = this.arrayOfLogics.filter(x => x != undefined);
      arrayOfLogicsNoUndefined.shift();  //Remove the first item, because that was the default policy already registered.
      for (let i = 0; i < arrayOfLogicsNoUndefined.length; i++) {
        let additionalPolc: { policyNumber: string; zipCode: string; approve: boolean | null; active: boolean | null } = {
          policyNumber: '',
          zipCode: '',
          approve: null,
          active: null
        };
        additionalPolc.policyNumber = arrayOfLogicsNoUndefined[i].policy.trim();
        additionalPolc.zipCode = arrayOfLogicsNoUndefined[i].zip;
        additionalPolc.approve = arrayOfLogicsNoUndefined[i].approved ? true : false;
        additionalPolc.active = arrayOfLogicsNoUndefined[i].active ? true : false;
        request.additionalPolicies.push(additionalPolc);
      }
    }//proceed to register
    this.submitUser(request);
  }

  submitUser(request: any) {
    this.userService.createUser(request).subscribe(
      data => {
        this.apiInProgress = false;
        if (data && data?.message == 'One or more errors occurred. (Save Policy Failure but Created User)') {
          this.multiPolicySaveHadAnError = true;
          this.userCreatedSuccessfully = true;
        }
        if (data && data?.message == 'One or more errors occurred. (Some policies require approval but Created User)') {
          this.multiPolicySaveApprovalError = true;
          this.userCreatedSuccessfully = true;
        }
        if (data && data?.message == 'User already exists. User Not Created.') {
          this.userCreatedSuccessfully = true;
        }
        if (data && data?.message == 'User created successfully') {
          this.userCreatedSuccessfully = true;
        } else {
          this.apiInProgress = false;
          this.dataError = true;
        }

      }, error => {
        this.apiInProgress = false;
        if ((error.error != null && error.error.errorMessage !== undefined) && ((error.error.errorMessage === 'InvalidInput for field/s.') || (error.error.errorMessage === 'Invalid Input'))) {
          this.scriptError = true;
        } else {
          this.scriptError = false;
          this.handleError(error);
        }
      }
    );
    this.taggingService.link({
      completion_event: '5026'
    });
  }

  handleError(err: any) {
    this.apiInProgress = false;
    this.systemError = true;
    this.logInterface.routeName = this.router.url;
    this.logInterface.exceptionMessage = err.toString();
    this.utilityService.uiLog(this.logInterface).subscribe(
      data => { }, error => { }
    );
  }

  tagging() {
    this.taggingService.link({
      link_name: 'login'
    });
  }

  cancel() {
    let locale = sessionStorage.getItem('locale');
    if (locale && locale != 'en') {
      this.auth.logout({
        logoutParams: {
          returnTo: `${ENV.auth0.defaultLogoutUrl}login/${locale}`
        }
      });
    } else {
      this.auth.logout({
        logoutParams: {
          returnTo: ENV.auth0.defaultLogoutUrl
        }
      });
    }
  }

  goToLoginPage() {
    let locale = sessionStorage.getItem('locale');
    if (locale != 'en') {
      this.router.navigate([`/login/${locale}`]); //Make sure international users don't go to the SSO page.
    } else {
      this.router.navigate(['/login']);
    }
  }

  loadLinksFromLocale() {
    let locale = sessionStorage.getItem('locale');
    if (locale == 'en-DE') {
      this.deprivacyNotice = ENV.links.deprivacyNotice;
    }
    else if (locale == 'de-DE') {
      this.deprivacyNotice = ENV.links.deDeprivacyNotice;
    }
    else if (locale == 'en-FR') {
      this.frprivacyNotice = ENV.links.frprivacyNotice;
    }
    else if (locale == 'fr-FR') {
      this.frprivacyNotice = ENV.links.frFrprivacyNotice;
    }
    else if (locale == 'en-IT') {
      this.itprivacyNotice = ENV.links.itprivacyNotice;
    }
    else if (locale == 'it-IT') {
      this.itprivacyNotice = ENV.links.itItprivacyNotice;
    }
  }

  showPolicy2() {
    this.showMultiPolicy = true;

    this.policy2.setValidators([Validators.required, Validators.minLength(6), Validators.pattern(/^(?!(623876))/)]);
    this.zip2.setValidators([Validators.required, Validators.pattern(ALPHA_NUMERIC_AND_SPACES_PATTERN)]);
  }

  togglePolicy3() {
    this.showAddPolicy3 = !this.showAddPolicy3;
    this.showPolicy3 = !this.showPolicy3;

    if (this.showPolicy3) {
      this.policy3.setValidators([Validators.required, Validators.minLength(6), Validators.pattern(/^(?!(623876))/)]);
      this.zip3.setValidators([Validators.required, Validators.pattern(ALPHA_NUMERIC_AND_SPACES_PATTERN)]);
    } else {
      this.policy3.setValidators(null);
      this.zip3.setValidators(null);
    }
  }

  //Validates one or multiple policies (2)
  policyValidate() {
    let responseArray = [];

    //send policy zip combos into API requests, of which the observables are pushed into an array
    if (this.showMultiPolicy) {
      responseArray.push(this.accountService.policyCheck(this.policy.value.trim(), this.zip.value.trim()));
      this.arrayOfLogics.push({ policy: this.policy.value, zip: this.zip.value });
      responseArray.push(this.accountService.policyCheck(this.policy2.value.trim(), this.zip2.value.trim()));
      this.arrayOfLogics.push({ policy: this.policy2.value, zip: this.zip2.value });
      if (this.showPolicy3) {
        responseArray.push(this.accountService.policyCheck(this.policy3.value.trim(), this.zip3.value.trim()));
        this.arrayOfLogics.push({ policy: this.policy3.value, zip: this.zip3.value });
      }
    } else {  //singular policy
      responseArray.push(this.accountService.policyCheck(this.policy.value.trim(), this.zip.value.trim()))
      this.arrayOfLogics.push({ policy: this.policy.value.trim() });
    }

    //forkjoin the array of observables and subscribe to the result, it will only notify once all API requests finish
    forkJoin(responseArray).subscribe(
      data => {
        let copyOfData: any = data;
        let trues = copyOfData.filter((x: any) => x?.data?.isValid == true).length; //The count of "true"
        if ((this.showMultiPolicy && this.showPolicy3 && trues == 3) || (this.showMultiPolicy && !this.showPolicy3 && trues == 2) || (!this.showMultiPolicy && trues == 1)) { //pass
          //Check user roles and digital wallets
          this.checkRoleUsersAndDigitalWallet();
          this.scriptError = false;
          this.systemError = false;
        } else {  //fail
          for (let i = 0; i < copyOfData.length; i++) {
            if (!copyOfData[i].data.isValid) {
              if (copyOfData[i].message && copyOfData[i].message.toLowerCase() == 'cannot be contract id') {
                if (i == 0) {
                  this.multipolicyError1 = 'It looks like you entered a Contract number associated to a Policy, please enter the Policy number.';
                } else if (i == 1) {
                  this.multipolicyError2 = 'It looks like you entered a Contract number associated to a Policy, please enter the Policy number.';
                } else if (i == 2) {
                  this.multipolicyError3 = 'It looks like you entered a Contract number associated to a Policy, please enter the Policy number.';
                }
              } else {
                //Some other error occurred
                if (i == 0) {
                  this.multipolicyError1 = 'Either the Policy Number or Zip Code is invalid';
                } else if (i == 1) {
                  this.multipolicyError2 = 'Either the Policy Number or Zip Code is invalid';
                } else if (i == 2) {
                  this.multipolicyError3 = 'Either the Policy Number or Zip Code is invalid';
                }
              }
            } else {
              //isValid was true, so there should be no error on this record
              if (i == 0) {
                this.multipolicyError1 = '';
              } else if (i == 1) {
                this.multipolicyError2 = '';
              } else if (i == 2) {
                this.multipolicyError3 = '';
              }
            }
          }
          this.apiInProgress = false;
          // this.errorReturned = true;
        }
      }, error => {
        this.apiInProgress = false;
        if ((error.error != null && error.error.errorMessage !== undefined) && ((error.error.errorMessage === 'InvalidInput for field/s.') || (error.error.errorMessage === 'Invalid Input'))) {
          this.scriptError = true;
        } else {
          this.systemError = true;
        }
      }
    );
  }

  private errorMessage(data_res: any) {
    var message = "";
    if (data_res?.data?.isValid === false && data_res?.message === "Cannot be Contract Id") {  // If isValid is explicitly false
      message = "It looks like you entered a Contract number associated to a Policy, please enter the Policy number.";
      this.apiInProgress = false;
      this.errorReturned = false;
      this.isInvalidData = true;
    } else if (data_res?.data === null) {  // If data is explicitly null
      message = "It looks like the Policy, Contract or zip you entered is incorrect.";
      this.apiInProgress = false;
      this.errorReturned = false;
      this.isNullData = true;
    } else if (data_res?.data?.isValid === true) {
      message = "";
      this.apiInProgress = false;
      this.errorReturned = false;
    } else {  // Handle other unexpected responses
      message = "Either the Policy Number or Zip Code is invalid";
      this.apiInProgress = false;
      this.errorReturned = true;
    }
    return message;
  }

  processToken() {
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (claims: IdToken | null | undefined) => {
        if (claims) {
          this.loginService.processToken(claims.__raw).subscribe(
            data => {
              if (data && data.policyNumber) {
                this.router.navigate(['/login']);
              }
              this.userService.setUserInfo(data); //store the baseline token details, as it has the user's email.
              this.userDetails = data;
            }, error => { }
          );
        }
      }
    );
  }

  determineLoginState() {
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (claims: IdToken | null | undefined) => {
        if (claims && claims?.sub && claims?.sub?.startsWith('email|')) {
          //User most likely came from a guest flow, has a passwordless auth, but needs to login to their actual account now.
          this.logout();  //invalidate their passwordless token
          return;
        }

        if (claims) {
          this.loginService.processToken(claims.__raw).subscribe(
            data => {
              if (data.policyNumber) {
                this.router.navigate(['/login']);
              } else if (!this.userDetails || !this.userDetails?.emailAddress) {
                this.loginWithRedirect();
              } else {
                this.userService.setUserInfo(data);
                this.userDetails = data;
              }
            }
          )
        } else {
          this.loginWithRedirect();
        }
      }, error => {
        this.loginWithRedirect();
      }
    );
  }

  //Takes the user to the Auth0 login page, config is setup in environment file.
  loginWithRedirect() {
    if (this.auth0locale && this.auth0locale == 'en-CA') {
      this.auth.loginWithRedirect({
        authorizationParams: {
          ui_locales: this.auth0locale,  //Set the locale here as needed
          redirect_uri: environment.auth0.authorizationParams.redirect_uri,
          'ext-style': 'canada'
        }
      });
    } else {
      this.auth.loginWithRedirect({
        authorizationParams: {
          ui_locales: this.auth0locale,  //Set the locale here as needed
          redirect_uri: environment.auth0.authorizationParams.redirect_uri,
          'ext-lang': this.locale,
          acr_values: this.locale
        }
      });
    }
  }

  determineLocale() {
    this.userService.getLocale().subscribe(
      locale => {
        if (locale && locale.toLowerCase() != 'en-ca') {
          this.auth0locale = locale.split('-')[0]; //Get only the language part
          this.locale = locale;
        } else if (locale && locale.toLowerCase() == 'en-ca') {
          this.auth0locale = 'en-CA';  //They do want hyphen part for en-CA. You can find exactly what they are looking for each language when going to Autho0 homepage, and the Settings in the bottom left corner. Tenant Settings, and then at the very bottom of those are the language options.
          this.locale = locale;
        } else {
          this.auth0locale = 'en'; //Default to English
          this.locale = 'en';
        }
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }, error => {
        this.auth0locale = 'en'; //Default to English
        this.locale = 'en';
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }
    );
  }

  logout() {
    this.auth.logout({
      logoutParams: {
        returnTo: environment.auth0.defaultLogoutUrl
      }
    });
  }

  get policy() { return this.accountFormGroup.controls.policyNumberFormControl }
  get policy2() { return this.accountFormGroup.controls.policy2 }
  get policy3() { return this.accountFormGroup.controls.policy3 }
  get zip() { return this.accountFormGroup.controls.zipFormControl; }
  get zip2() { return this.accountFormGroup.controls.zip2; }
  get zip3() { return this.accountFormGroup.controls.zip3; }
  get fName() { return this.accountFormGroup.controls.firstName; }
  get lName() { return this.accountFormGroup.controls.lastName; }
}
